import { PortalConfig } from '@epilot/customer-portal-client'
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useQueries
} from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { CUSTOMER_QUERY_IDS } from 'api/Queries'

import { embeddedListener } from './communication'

let dummyMode = true
let previewProxyMode = false
let builderOrigin: string | null = null
let referrerOrigin: string | null = null

// Capture builder origin from document.referrer on startup
try {
  if (typeof window !== 'undefined' && window.parent !== window) {
    referrerOrigin = new URL(document.referrer).origin
    builderOrigin = referrerOrigin
  }
} catch {
  // referrer may be empty or invalid, or window may not exist (tests/SSR)
}

export const getBuilderOrigin = (): string | null => builderOrigin

export const getReferrerOrigin = (): string | null => referrerOrigin

export const setBuilderOrigin = (origin: string) => {
  builderOrigin = origin
}

/**
 * Get the validated parent origin for postMessage sends.
 * Falls back to document.referrer origin. Never returns '*'.
 */
export const getParentOrigin = (): string => {
  if (builderOrigin) return builderOrigin
  if (referrerOrigin) return referrerOrigin

  // Should not happen in normal flow — log a warning for debugging
  console.warn('[ECP] No parent origin available for postMessage')

  return referrerOrigin || builderOrigin || ''
}

export type ExtendedPortalConfig = PortalConfig & { is_dummy?: boolean }

export const isInDummyMode = (defaultConfig?: ExtendedPortalConfig) => {
  const isEmbedded = window.parent !== window

  if (!isEmbedded) return false

  if (defaultConfig?.is_dummy) {
    dummyMode = true
  }

  return dummyMode
}

/**
 * Whether blocks should use hardcoded dummy data (e.g. DUMMY_CONTACT).
 * Returns true only in pure dummy mode (builder without real customer preview).
 * Returns false when real preview data is active or when not in dummy mode.
 */
export const shouldUseDummyData = () => isInDummyMode() && !previewProxyMode

/**
 * Preview proxy mode: the portal is embedded and entity data queries
 * pass through to the real API via the builder's caching proxy.
 * Builder-managed queries (config, pages, schemas) still use DUMMY_MOCKS.
 */
export const isPreviewProxyMode = () => previewProxyMode

export const PREVIEW_PROXY_MODE_EVENT = 'ecp-preview-proxy-mode-change'

export const setPreviewProxyMode = (enabled: boolean) => {
  previewProxyMode = enabled
  window.dispatchEvent(
    new CustomEvent(PREVIEW_PROXY_MODE_EVENT, { detail: { enabled } })
  )
}

/**
 * Query keys that always return mock data, even in preview proxy mode.
 * These are pushed by the builder via postMessage (config, pages, schemas, etc.)
 * because the portal can't fetch them on its own in embedded mode.
 * All other queries pass through to the real API via the proxy adapter.
 */
const MOCK_ONLY_KEYS = new Set([
  CUSTOMER_QUERY_IDS.GET_PORTAL_CONFIG,
  CUSTOMER_QUERY_IDS.GET_PAGES,
  CUSTOMER_QUERY_IDS.GET_PUBLIC_PAGES,
  CUSTOMER_QUERY_IDS.GET_SCHEMAS,
  CUSTOMER_QUERY_IDS.GET_ORGANIZATION_SETTINGS,
  CUSTOMER_QUERY_IDS.GET_PUBLIC_EXTENSIONS,
  CUSTOMER_QUERY_IDS.GET_PORTAL_WIDGETS,
  CUSTOMER_QUERY_IDS.GET_PUBLIC_PORTAL_WIDGETS
])

function isMockOnlyKey(queryKey: string): boolean {
  return MOCK_ONLY_KEYS.has(queryKey)
}

let previewLoading = false

export const PREVIEW_LOADING_EVENT = 'ecp-preview-loading-change'

export const setPreviewLoading = (loading: boolean) => {
  previewLoading = loading
  window.dispatchEvent(
    new CustomEvent(PREVIEW_LOADING_EVENT, { detail: { loading } })
  )
}

export const isPreviewLoading = () => previewLoading

let isListenerAdded = false

export const getDummyApi = async () => {
  const isDummy = isInDummyMode()

  if (!isDummy) return null

  if (!isListenerAdded) {
    window.addEventListener('message', embeddedListener)
    isListenerAdded = true
    window.addEventListener('beforeunload', () => {
      window.removeEventListener('message', embeddedListener)
      isListenerAdded = false // Reset flag
    })
  }

  return import('./index')
}

export function useQueryWithMock<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends string[] = string[]
>({
  queryKey,
  queryFn,
  ...params
}: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<
  TData,
  TError
> {
  return useQuery({
    // @ts-expect-error Unfortunately it cannot match it against any of the overloads - hopefully it's fixed with v5
    queryKey,
    queryFn: withDummyMock(queryFn, queryKey?.[0]),
    ...params
  })
}

export function useQueriesWithMock({ queries }: { queries: any[] }) {
  return useQueries({
    queries: queries.map((query) => {
      if (!query) return null

      return {
        ...query,
        queryFn: withDummyMock(query.queryFn, query.queryKey?.[0])
      }
    })
  })
}

export function withDummyMock<T>(
  queryFn: T,
  queryKey: string,
  response?: Partial<AxiosResponse>
) {
  const isDummy = isInDummyMode()

  if (!isDummy) return queryFn

  // In preview proxy mode, let entity data queries pass through to the
  // proxy adapter on the axios client. Only mock-only keys (config, pages,
  // schemas, etc.) continue using mock data from DUMMY_MOCKS.
  if (previewProxyMode && !isMockOnlyKey(queryKey)) {
    return queryFn
  }

  // Mock mode: return data from DUMMY_MOCKS
  return async () => {
    const Dummy = await getDummyApi()

    const matchedMock = Dummy.getMock(queryKey)

    if (matchedMock) {
      return {
        data: matchedMock,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {},
        ...response
      } as AxiosResponse
    } else {
      return {
        data: null,
        status: 404,
        statusText: 'Not Found',
        headers: {},
        config: {},
        ...response
      } as AxiosResponse
    }
  }
}

export const useDummyNavigationBlock = () => {
  const isDummy = isInDummyMode()
  const history = useHistory()

  useEffect(() => {
    if (!isDummy) {
      return () => {}
    }

    const unblock = history.block(({ search, pathname }) => {
      if (pathname !== history.location.pathname) {
        return false
      }

      const nextParams = new URLSearchParams(search)
      const currentParams = new URLSearchParams(history.location.search)

      if (nextParams.get('block-id') !== currentParams.get('block-id')) {
        return undefined
      }

      for (const key of new Set([
        ...Array.from(nextParams.keys()),
        ...Array.from(currentParams.keys())
      ])) {
        if (key === 'tab' || key.startsWith('tab-')) {
          if (nextParams.get(key) !== currentParams.get(key)) {
            return undefined
          }
        }
      }

      return false
    })

    return () => {
      unblock()
    }
  }, [isDummy, history])
}
